import { useEffect, useState } from 'react'

import { get, put, post } from '@/api/api'
import { stringifyParams } from '@/lib/helpers'

type LazyEndpointData = {
  method: 'GET' | 'PUT' | 'POST'
  url: string
}

type EndpointCallParams = any

type EndpointData = any

type EndpointResponse = {
  isLoading: boolean
  data: any
  error: any
  isUninitialized: boolean
}

type UseEndpoint = ({ method, url, body }: EndpointData) => EndpointResponse

export const useEndpoint: UseEndpoint = ({ method, url, body }) => {
  const [call, { isLoading, data, error, isUninitialized }] = useLazyEndpoint({
    method,
    url,
  })
  useEffect(() => {
    call(body)
  }, [call, method, url, body])
  return { isLoading, data, error, isUninitialized }
}

type UseLazyEndpoint = (
  params: LazyEndpointData
) => [(data: EndpointData) => void, EndpointResponse]

export const useLazyEndpoint: UseLazyEndpoint = ({ method, url }) => {
  const fullUrl = `${process.env.API_BASE_URL}${url}`
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<unknown>(null)
  const [error, setError] = useState<unknown>(null)

  const onSuccess = (data: unknown) => {
    setData(data)
  }

  const onError = (error: unknown) => {
    setError(error)
  }

  const onFinally = () => {
    setIsLoading(false)
  }

  const call = (body: EndpointCallParams) => {
    if (isLoading || data || error) return
    if (method === 'GET') {
      setIsLoading(true)
      get(`${fullUrl}${stringifyParams(body)}`)
        .then(onSuccess)
        .catch(onError)
        .finally(onFinally)
    } else if (method === 'PUT') {
      setIsLoading(true)
      put(fullUrl, body).then(onSuccess).catch(onError).finally(onFinally)
    } else if (method === 'POST') {
      setIsLoading(true)
      post(fullUrl, body).then(onSuccess).catch(onError).finally(onFinally)
    }
  }

  const isUninitialized = !isLoading && !data && !error

  return [call, { isLoading, data, error, isUninitialized }]
}
